import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Button,
  Box,
  Menu,
  Backdrop,
  CircularProgress,
  Divider,
  Tabs,
  Tab
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import CaptionItem from "./CaptionItem";
import AssetExplorer from "../common/AssetExplorer";
import FolderSelector from "../FolderSelector";

const SceneForm = ({
  open,
  onClose,
  isEditing,
  newScene,
  organizations,
  onSceneChange,
  onAddCaption,
  onCaptionChange,
  onFileChange,
  onRemoveCaption,
  onSave,
  onVersionHistoryClick,
  versionMenu,
  onVersionMenuClose,
  onVersionSelect,
  uploading,
  selectedOrgId,
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [activeFileIndex, setActiveFileIndex] = useState(-1);
  const [selectedLoadPartPath, setSelectedLoadPartPath] = useState(newScene.load_part_path || null);
  const dialogTitle = isEditing ? "Edit Scene" : "Add New Scene";

  const orgName = React.useMemo(() => {
    const org = organizations.find(org => org._id === selectedOrgId);
    return org?.name?.toLowerCase().replace(/\s+/g, "-") || '';
  }, [organizations, selectedOrgId]);

  const scenePathName = React.useMemo(() => {
    return newScene.scene_name?.toLowerCase().replace(/\s+/g, "-") || '';
  }, [newScene.scene_name]);

  useEffect(() => {
    setSelectedLoadPartPath(newScene.load_part_path || null);
  }, [newScene.load_part_path]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleOpenFileBrowser = () => {
    if (isEditing) {
      setActiveFileIndex(-1);
      setTabValue(1);
    }
  };


  const handleFileSelect = (file) => {
    if (activeFileIndex >= 0) {
      onFileChange(activeFileIndex, {
        name: file.name,
        size: file.size,
        type: file.name.split('.').pop(),
        lastModified: new Date(file.lastModified).getTime(),
        s3Path: file.s3Path,
        isS3File: true,
        fileName: file.name
      });
    }
    setTabValue(0);
  };

  const handleFolderSelect = (folder) => {
    
    let loadPartPath = null;
    if (folder && folder.path) {
      const pathParts = folder.path.split('/');
      const assetsIndex = pathParts.indexOf('assets');
      
      if (assetsIndex >= 0) {
        loadPartPath = pathParts.slice(assetsIndex + 1).join('/').replace(/\/$/, '');
      }
    }   
    setSelectedLoadPartPath(loadPartPath);
    
    onSceneChange({
      target: {
        name: 'load_part_path',
        value: loadPartPath
      }
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          height: '70vh',
          display: 'flex',
          flexDirection: 'column',
        }
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
        {dialogTitle}
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{
            px: 2,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              color: '#CCCCCC',
            }
          }}
        >
          <Tab label="Scene Details" id="scene-tab-0" />
          {isEditing && <Tab label="Asset Explorer" id="scene-tab-1" />}
        </Tabs>
      </Box>

      <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
        {tabValue === 0 ? (
          <DialogContent sx={{ height: '100%', overflowY: 'auto' }}>
            <TextField
              autoFocus
              margin="dense"
              name="scene_name"
              label="Scene Name"
              type="text"
              fullWidth
              value={newScene.scene_name || ""}
              onChange={(e) => onSceneChange(e)}
              disabled={isEditing}
            />

            <FormControl fullWidth margin="dense">
              <InputLabel sx={{ color: '#CCCCCC90' }}>Organisation</InputLabel>
              <Select
                name="organization_id"
                value={selectedOrgId}
                label={'Organisation'}
                disabled={true}
              >
                {organizations.map((org) => (
                  <MenuItem key={org._id} value={org._id}>
                    {org.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {isEditing && (
              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ color: '#CCCCCC', mb: 1 }}>
                  Load Part Folder
                </Typography>
                <FolderSelector
                  scenePath={`scenes/${orgName}/${scenePathName}`}
                  rendererId={null} 
                  onFolderSelect={handleFolderSelect}
                  title="Select Load Part Folder"
                  placeholder="Choose a folder from the scene assets..."
                  selectedPath={selectedLoadPartPath}
                  organizationName={orgName}
                  sceneName={scenePathName}
                />
              </Box>
            )}

            {/* Scene-level Assets Button */}
            {/* {isEditing &&
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 1 }}>
                <Button
                  variant="outlined"
                  color="info"
                  startIcon={<FolderOpenIcon />}
                  onClick={handleOpenFileBrowser}
                >
                  Manage Assets
                </Button>
              </Box>} */}

            <Typography
              variant="h6"
              sx={{
                mt: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2
              }}
            >
              Captions
              <Button
                color="seafoamGreen"
                variant="outlined"
                onClick={onAddCaption}
                startIcon={<AddCircleOutlineIcon />}
                sx={{ mt: 2 }}
              >
                Add Caption
              </Button>
            </Typography>

            {newScene.captions.map((caption, index) => (
              <Box key={index} sx={{ position: 'relative', mb: 2 }}>
                <CaptionItem
                  caption={caption}
                  index={index}
                  isEditing={isEditing}
                  onCaptionChange={onCaptionChange}
                  onFileChange={onFileChange}
                  onRemoveCaption={onRemoveCaption}
                  onVersionHistoryClick={onVersionHistoryClick}
                  availableVersions={caption.all_versions || []}
                />
                {/* Browse Files button removed from here */}
              </Box>
            ))}

            <Menu
              anchorEl={versionMenu.anchorEl}
              open={versionMenu.open}
              onClose={onVersionMenuClose}
            >
              <Box sx={{ p: 2, minWidth: "200px" }}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Select Version</FormLabel>
                  <RadioGroup
                    value={
                      versionMenu.captionIndex !== -1
                        ? newScene.captions[versionMenu.captionIndex]?.version
                        : ""
                    }
                    onChange={(e) => onVersionSelect(e.target.value)}
                  >
                    {versionMenu.versions.map((version, idx) => (
                      <FormControlLabel
                        key={idx}
                        value={version}
                        control={<Radio />}
                        label={version}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </Box>
            </Menu>
          </DialogContent>
        ) : (
          <Box sx={{ height: '100%', p: 2 }}>
            <AssetExplorer
              onFileSelect={handleFileSelect}
              organizationName={orgName}
              sceneName={scenePathName}
            />
          </Box>
        )}
      </Box>

      <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <Button onClick={onClose} variant="outlined" color="info">
          Cancel
        </Button>
        <Button
          onClick={onSave}
          variant="contained"
          color="seafoamGreen85"
          sx={{ fontWeight: 'bold' }}
          disabled={uploading}
        >
          {isEditing ? "Update" : "Save"}
        </Button>
      </DialogActions>

      {uploading && (
        <Backdrop
          open={uploading}
          sx={{
            color: "#fff",
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress color="inherit" />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Uploading Captions, please wait...
            </Typography>
          </Box>
        </Backdrop>
      )}
    </Dialog>
  );
};
export default SceneForm;